<template>
  <form
    class="lms-workflow"
    :method="form.method || 'post'"
    :action="form.action"
    @submit="onSubmit"
  >
    <input type="hidden" name="authenticity_token" :value="csrfToken">
    <input
      v-for="(val, key) in form.hidden || {}"
      :key="key"
      type="hidden"
      :name="key"
      :value="val"
    >
    <input
      v-if="selectionParam"
      type="hidden"
      :name="selectionParam"
      :value="selectedIdsJoined"
    >

    <div class="lms-workflow__head">
      <button
        type="button"
        class="lms-workflow__toggle"
        :aria-expanded="expanded ? 'true' : 'false'"
        @click="expanded = !expanded"
      >
        <span>
          <span class="lms-workflow__title">{{ form.title }}</span>
          <span v-if="form.subtitle" class="lms-workflow__sub">{{ form.subtitle }}</span>
        </span>
        <v-icon
          :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          size="20"
          aria-hidden="true"
        />
      </button>
    </div>

    <v-expand-transition>
      <div v-if="expanded">
        <div class="lms-workflow__fields">
          <div
            v-for="field in form.fields || []"
            :key="field.name"
            class="lms-workflow__field"
            :style="fieldStyle(field)"
          >
        <template v-if="field.type === 'info'">
          <p class="lms-workflow__info">{{ field.label }}</p>
        </template>

        <template v-else-if="field.type === 'checkbox'">
          <v-checkbox
            v-model="local[field.name]"
            true-value="1"
            false-value=""
            :label="field.optionLabel || field.label"
            :required="field.required"
            density="compact"
            hide-details="auto"
          />
          <input type="hidden" :name="fieldName(field)" :value="local[field.name] || ''">
        </template>

        <template v-else-if="field.type === 'select' || field.type === 'select2' || field.type === 'radio' || field.type === 'nested_select'">
          <v-select
            v-model="local[field.name]"
            :items="field.choices || []"
            item-title="label"
            item-value="value"
            :label="field.label"
            :required="field.required"
            :multiple="!!field.multiple || field.type === 'multi-select'"
            density="compact"
            variant="outlined"
            hide-details="auto"
          />
          <template v-if="Array.isArray(local[field.name])">
            <input
              v-for="(v, i) in local[field.name]"
              :key="`${field.name}-${i}`"
              type="hidden"
              :name="`${fieldName(field)}[]`"
              :value="v"
            >
          </template>
          <input
            v-else
            type="hidden"
            :name="fieldName(field)"
            :value="local[field.name] ?? ''"
          >
        </template>

        <template v-else-if="field.type === 'multi-select'">
          <v-select
            v-model="local[field.name]"
            :items="field.choices || []"
            item-title="label"
            item-value="value"
            :label="field.label"
            multiple
            chips
            density="compact"
            variant="outlined"
            hide-details="auto"
          />
          <input
            v-for="(v, i) in (local[field.name] || [])"
            :key="`${field.name}-${i}`"
            type="hidden"
            :name="`${fieldName(field)}[]`"
            :value="v"
          >
        </template>

        <template v-else-if="field.type === 'date_range'">
          <div class="lms-workflow__range">
            <v-text-field
              v-model="rangeOf(field.name).start"
              :label="`${field.label || 'Date'} start`"
              type="date"
              density="compact"
              variant="outlined"
              hide-details="auto"
            />
            <v-text-field
              v-model="rangeOf(field.name).end"
              :label="`${field.label || 'Date'} end`"
              type="date"
              density="compact"
              variant="outlined"
              hide-details="auto"
            />
          </div>
          <input type="hidden" :name="`${fieldName(field)}[start]`" :value="rangeOf(field.name).start || ''">
          <input type="hidden" :name="`${fieldName(field)}[end]`" :value="rangeOf(field.name).end || ''">
        </template>

        <template v-else-if="field.type === 'month_range'">
          <div class="lms-workflow__range">
            <v-text-field
              v-model="rangeOf(field.name).start"
              :label="`${field.label || 'Month'} start`"
              type="month"
              density="compact"
              variant="outlined"
              hide-details="auto"
            />
            <v-text-field
              v-model="rangeOf(field.name).end"
              :label="`${field.label || 'Month'} end`"
              type="month"
              density="compact"
              variant="outlined"
              hide-details="auto"
            />
          </div>
          <input type="hidden" :name="`${fieldName(field)}[start]`" :value="rangeOf(field.name).start || ''">
          <input type="hidden" :name="`${fieldName(field)}[end]`" :value="rangeOf(field.name).end || ''">
        </template>

        <template v-else-if="field.type === 'text'">
          <v-textarea
            v-model="local[field.name]"
            :label="field.label"
            :placeholder="field.placeholder || ''"
            :required="field.required"
            rows="3"
            auto-grow
            density="compact"
            variant="outlined"
            hide-details="auto"
          />
          <input type="hidden" :name="fieldName(field)" :value="local[field.name] ?? ''">
        </template>

        <template v-else>
          <v-text-field
            v-model="local[field.name]"
            :label="field.label"
            :placeholder="field.placeholder || ''"
            :type="inputType(field)"
            :required="field.required"
            density="compact"
            variant="outlined"
            hide-details="auto"
          />
          <input type="hidden" :name="fieldName(field)" :value="local[field.name] ?? ''">
        </template>
          </div>
        </div>

        <div class="lms-workflow__actions">
          <v-btn
            type="submit"
            color="primary"
            variant="flat"
            :disabled="submitDisabled"
            :loading="submitting"
          >
            {{ form.submitLabel || 'Submit' }}
          </v-btn>
          <span v-if="form.requireSelection" class="lms-workflow__hint">
            {{ selectedCount }} selected
          </span>
          <span
            v-if="submitDisabled && form.requireAllFields && selectedCount"
            class="lms-workflow__hint"
          >
            Fill all template fields
          </span>
        </div>
      </div>
    </v-expand-transition>
  </form>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'

const props = defineProps({
  form: { type: Object, required: true },
  selectedIds: { type: Array, default: () => [] },
  allIds: { type: Array, default: () => [] }
})

const emit = defineEmits(['before-submit'])

const submitting = ref(false)
const expanded = ref(true)
const local = reactive({})

const csrfToken = computed(() =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
)

const selectionParam = computed(() => props.form.selectionParam || null)

const selectedIdsJoined = computed(() => {
  if (props.form.requireSelection) return (props.selectedIds || []).join('_')
  if (props.form.useAllIds) return (props.allIds || []).join('_')
  return (props.selectedIds || []).join('_')
})

const selectedCount = computed(() => (props.selectedIds || []).length)

const submitDisabled = computed(() => {
  if (props.form.requireSelection && !selectedCount.value) return true
  const fields = props.form.fields || []
  const requireAllFields = !!props.form.requireAllFields
  return fields.some((f) => {
    if (f.type === 'info' || f.type === 'checkbox') return false
    if (!requireAllFields && !f.required) return false
    if (f.type === 'date_range' || f.type === 'month_range') {
      const range = local[f.name] || {}
      return !range.start || !range.end
    }
    const val = local[f.name]
    return val == null || String(val).trim() === '' || (Array.isArray(val) && !val.length)
  })
})

watch(
  () => props.form,
  (form) => {
    Object.keys(local).forEach((k) => { delete local[k] })
    ;(form.fields || []).forEach((f) => {
      if (f.type === 'info') return
      if (f.type === 'date_range' || f.type === 'month_range') {
        local[f.name] = {
          start: f.value?.start || '',
          end: f.value?.end || ''
        }
      } else if (f.type === 'multi-select' || f.multiple) {
        local[f.name] = Array.isArray(f.value) ? [...f.value] : []
      } else if (f.type === 'checkbox') {
        local[f.name] = (f.value === true || f.value === '1' || f.value === 1) ? '1' : ''
      } else if (f.type === 'select' || f.type === 'select2' || f.type === 'radio' || f.type === 'nested_select') {
        if (f.value != null && f.value !== '') {
          local[f.name] = f.value
        } else if (f.required && f.includeBlank === false) {
          local[f.name] = f.choices?.[0]?.value ?? ''
        } else {
          local[f.name] = f.value ?? ''
        }
      } else {
        local[f.name] = f.value ?? ''
      }
    })
  },
  { immediate: true, deep: true }
)

function rangeOf (name) {
  if (!local[name] || typeof local[name] !== 'object') {
    local[name] = { start: '', end: '' }
  }
  return local[name]
}

function fieldName (field) {
  return props.form.paramPrefix
    ? `${props.form.paramPrefix}[${field.name}]`
    : `options[${field.name}]`
}

function fieldStyle (field) {
  const cols = Number(field.cols) || 6
  const width = Math.min(100, Math.max(25, (cols / 12) * 100))
  return { flex: `1 1 ${width}%`, minWidth: cols >= 12 ? '100%' : '220px' }
}

function inputType (field) {
  if (field.type === 'number') return 'number'
  if (field.type === 'date' || field.type === 'date_future') return 'date'
  if (field.type === 'email') return 'email'
  return 'text'
}

function onSubmit (e) {
  if (submitDisabled.value) {
    e.preventDefault()
    return
  }
  const confirmMessage = props.form.confirmMessage
  if (confirmMessage && !window.confirm(confirmMessage)) {
    e.preventDefault()
    return
  }
  emit('before-submit', props.form)
  submitting.value = true
}
</script>

<style scoped>
.lms-workflow {
  border: 1px solid var(--lms-border);
  border-radius: 12px;
  padding: 14px 16px;
  background: var(--app-surface);
  margin-bottom: 12px;
}
.lms-workflow__head { margin-bottom: 10px; }
.lms-workflow__toggle {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: left;
}
.lms-workflow__title {
  display: block;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--lms-text);
}
.lms-workflow__sub {
  display: block;
  margin-top: 2px;
  color: var(--lms-text-secondary);
  font-size: 0.8rem;
}
.lms-workflow__fields {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
}
.lms-workflow__info {
  margin: 0;
  color: var(--lms-text-secondary);
  font-size: 0.9rem;
}
.lms-workflow__range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.lms-workflow__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}
.lms-workflow__hint {
  font-size: 0.8rem;
  color: var(--lms-text-secondary);
}
</style>
